/**
 * JsonImportSource Component
 *
 * Import source for parsing JSON data into schema fields.
 * Supports two formats:
 * 1. Schema definition: {fields: [{name, type, required, ...}]}
 * 2. Sample data object: {"temp": 72.5, "humidity": 45} - infers types from values
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SchemaField, SchemaFieldType, flattenJsonToLeaves } from '@/services/api/schemaApi';

export interface ParsedFieldGroups {
  /** Map of field id -> group label (empty string for top-level) */
  groupByFieldId: Record<string, string>;
  /** Ordered list of group labels as they should appear */
  groupOrder: string[];
}

interface JsonImportSourceProps {
  onFieldsParsed: (fields: SchemaField[], groups?: ParsedFieldGroups) => void;
}

export const JsonImportSource: React.FC<JsonImportSourceProps> = ({
  onFieldsParsed,
}) => {
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  // Generate unique field ID
  const generateFieldId = () => {
    return `field_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  };

  // Parse JSON input
  const handleParse = useCallback(() => {
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      return;
    }

    setIsParsing(true);
    setError(null);

    try {
      const parsed: unknown = JSON.parse(jsonInput);
      if (parsed == null || typeof parsed !== 'object') {
        throw new Error('Invalid format. Expected a JSON object');
      }
      let newFields: SchemaField[] = [];
      let groups: ParsedFieldGroups | undefined;
      const parsedObj = parsed as Record<string, unknown>;

      // Format 1: Schema definition with fields array
      if (parsedObj.fields && Array.isArray(parsedObj.fields)) {
        newFields = parsedObj.fields.map((f: Record<string, unknown>) => ({
          id: generateFieldId(),
          name: String(f.name || '').toLowerCase().replace(/\s/g, '_'),
          type: (String(f.type || 'string').toLowerCase() as SchemaFieldType) || 'string',
          required: Boolean(f.required),
          description: f.description ? String(f.description) : undefined,
          constraints: f.constraints as SchemaField['constraints'],
        }));
      }
      // Format 2: Sample data object - flatten nested structures into primitive leaves
      else if (!Array.isArray(parsed)) {
        const leaves = flattenJsonToLeaves(parsedObj);
        const groupByFieldId: Record<string, string> = {};
        const groupOrder: string[] = [];
        newFields = leaves.map((leaf) => {
          const id = generateFieldId();
          groupByFieldId[id] = leaf.groupLabel;
          if (!groupOrder.includes(leaf.groupLabel)) groupOrder.push(leaf.groupLabel);
          return {
            id,
            name: leaf.name,
            type: leaf.type as SchemaFieldType,
            required: false,
            description: undefined,
          };
        });
        groups = { groupByFieldId, groupOrder };
      } else {
        throw new Error('Invalid format. Expected object or {fields: [...]}');
      }

      if (newFields.length === 0) {
        throw new Error('No fields found in JSON data');
      }

      // Validate field names
      for (const field of newFields) {
        if (!field.name.trim()) {
          throw new Error('One or more fields have empty names');
        }
      }

      onFieldsParsed(newFields, groups);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON format');
    } finally {
      setIsParsing(false);
    }
  }, [jsonInput, onFieldsParsed]);

  return (
    <div className="space-y-3">
      <Textarea
        placeholder={`Paste JSON here...

Examples:
1. Sample data:
{"temperature": 72.5, "humidity": 45, "active": true}

2. Schema definition:
{"fields": [{"name": "temp", "type": "number", "required": true}]}`}
        value={jsonInput}
        onChange={(e) => {
          setJsonInput(e.target.value);
          setError(null);
        }}
        rows={6}
        className="font-mono text-sm"
      />

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleParse}
          disabled={!jsonInput.trim() || isParsing}
          className="bg-primary hover:bg-primary/90"
        >
          {isParsing ? 'Parsing...' : 'Parse JSON'}
        </Button>
      </div>
    </div>
  );
};

export default JsonImportSource;
